import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PeepingLogo } from "@/components/PeepingLogo";
import { MediaImage } from "@/components/Media";
import { LogOut, Eye, ShieldCheck, Menu, X, Radio } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const LINK_ACTIVE = "bg-gradient-to-r from-[#6a00ff]/30 to-[#d42be8]/20 text-white border border-[#d42be8]/30";
const LINK_IDLE = "text-[#a7a0af] hover:text-white hover:bg-white/5 border border-transparent";

// Shared shell for the Super Admin console and the Creator Studio.
export function StudioLayout({ nav, label, subtitle, testPrefix }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [avatar, setAvatar] = useState(null);
  const isCreator = user?.role === "creator";
  const { pathname } = useLocation();
  const [menu, setMenu] = useState(false);
  useEffect(() => { setMenu(false); }, [pathname]);
  const current = nav.find((n) => pathname === n.to || pathname.startsWith(`${n.to}/`));
  useEffect(() => {
    if (!isCreator) return undefined;
    const load = () => api.get("/creator/profile").then((r) => setAvatar(r.data.creator.avatar_url)).catch(() => {});
    load();
    window.addEventListener("peeping-profile-updated", load);
    return () => window.removeEventListener("peeping-profile-updated", load);
  }, [isCreator]);
  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const toReview = async () => {
    const { data } = await api.get("/admin/owner-review");
    if (!data.exists) {
      toast.message("Create the Owner Review account first.");
      return navigate("/admin/owner-review");
    }
    // Navigate first: logging out while still inside AdminRoute makes its guard redirect to a bare
    // /login, which would drop the ?email= prefill.
    navigate(`/login?email=${encodeURIComponent(data.account.email)}&switch=review`, { replace: true });
    await logout();
  };
  return (
    <div className="min-h-screen bg-[#080808] text-white flex flex-col lg:flex-row">
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0c0a10]">
        <div className="p-5 flex items-center justify-between lg:block">
          <PeepingLogo className="w-28 lg:w-36 items-start" showTagline={false} />
          <div className="flex items-center gap-2 lg:block">
            <span data-testid={`${testPrefix}-label`} className="text-[0.6rem] uppercase tracking-widest text-[#a7a0af] lg:mt-2 lg:block">{label}</span>
            {isCreator && (
              <button onClick={() => setMenu(!menu)} data-testid="creator-menu-toggle" aria-expanded={menu}
                className="flex items-center gap-1.5 rounded-xl border border-white/15 px-3 py-2 text-sm lg:hidden">
                {menu ? <X size={16} /> : <Menu size={16} />} {menu ? "Close" : "Menu"}
              </button>
            )}
          </div>
        </div>
        {isCreator && !menu && current && (
          <p data-testid="creator-current-section" className="px-5 pb-3 text-xs text-[#c9a6ff] lg:hidden">{current.label}</p>
        )}
        <nav data-testid={`${testPrefix}-nav`} className={isCreator
          ? `${menu ? "grid" : "hidden"} grid-cols-2 gap-1 px-3 pb-4 lg:flex lg:flex-col`
          : "px-3 pb-4 flex lg:flex-col gap-1 overflow-x-auto no-scrollbar"}>
          {nav.map(({ to, label: l, icon: Icon, end, id }) => (
            <NavLink key={to} to={to} end={end} data-testid={`${testPrefix}-nav-${id || l.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isCreator ? "min-h-[44px]" : "whitespace-nowrap"} ${isActive ? LINK_ACTIVE : LINK_IDLE}`}>
              <Icon size={18} className="shrink-0" /> <span className="leading-tight">{l}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            {avatar ? <MediaImage url={avatar} className="h-9 w-9 shrink-0 rounded-full object-cover" /> : <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-[#6a00ff] to-[#d42be8]" />}
            <div className="leading-tight min-w-0">
              <p data-testid={`${testPrefix}-username`} className="truncate text-sm font-semibold">{subtitle || user?.username}</p>
              <p className="text-[0.65rem] text-[#a7a0af]">{label}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "super_admin" && (
              <button onClick={toReview} data-testid="switch-to-review-btn"
                className="flex items-center gap-2 rounded-xl border border-[#d42be8]/40 bg-[#d42be8]/10 px-3 py-2 text-sm text-[#f0a8fa]">
                <ShieldCheck size={16} /> <span className="hidden sm:inline">Owner Review</span>
              </button>
            )}
            {isCreator && (
              <button onClick={() => navigate("/creator/live")} data-testid="creator-header-go-live"
                className="btn-brand flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-2 text-sm font-semibold">
                <Radio size={16} /> Go Live
              </button>
            )}
            <button onClick={() => navigate("/app/discover")} data-testid={`${testPrefix}-preview-app-btn`} title={isCreator ? "Subscriber view" : "Preview App"}
              className="flex items-center gap-2 rounded-xl border border-[#6a00ff]/40 bg-[#6a00ff]/10 px-3 py-2 text-sm text-[#c9a6ff] hover:border-[#d42be8]/50">
              <Eye size={16} /> <span className="hidden sm:inline">{isCreator ? "Subscriber view" : "Preview App"}</span>
            </button>
            <button onClick={doLogout} data-testid={`${testPrefix}-logout-btn`}
              className="flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm text-[#a7a0af] hover:text-white hover:border-white/25 transition-colors">
              <LogOut size={16} /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </header>
        <main className="flex-1 p-5 lg:p-8 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
